// ============================================================================
// OfflineBanner — global "you are offline" surface for QuoteMaker
// ----------------------------------------------------------------------------
// v5.40.0 (Peter 2026-05-22 UX audit): the audit Wave-1 P0 #5 found that
// `network-resilience.js` knows about online/offline transitions but
// nothing in the UI consumed the signal. From the user's perspective a
// network drop looked identical to "AI is thinking" — same spinner, no
// indication their work was queued.
//
// This component subscribes to `onConnectionChange` and renders a fixed
// amber strip at the top of the viewport while offline. Auto-hides on
// reconnect. Zero CSS dependencies (uses inline styles); zero a11y
// trade-offs (announced via `role="status"` + `aria-live="polite"`).
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Icons } from './Icon';
import { onConnectionChange, isOnline } from '../../services/network-resilience';

export default function OfflineBanner() {
    const [online, setOnline] = useState(() => isOnline());
    const [dismissed, setDismissed] = useState(false);

    // onConnectionChange returns its unsubscribe fn; cast (erases) so the
    // effect's return type satisfies useEffect's Destructor (the .js export
    // infers loosely as `Function`).
    useEffect(() => onConnectionChange((nextOnline) => {
        setOnline(nextOnline);
        if (nextOnline) setDismissed(false);
    }) as (() => void), []);

    if (online || dismissed) return null;

    return (
        // v5.40.13 (Wave 13 bug-hunt fix B5): dropped `position: fixed` + `top: 0`.
        // Banners are now stacked vertically by a flex-column wrapper in App.jsx
        // so they don't overlap when more than one is active simultaneously.
        <div
            role="status"
            aria-live="polite"
            style={{
                background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)',
                borderBottom: '2px solid #f59e0b',
                color: 'var(--qm-caution-900)',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '13px',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.25)',
            }}
        >
            <Icons.AlertTriangle size={16} style={{ color: '#b45309', flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
                QuoteMaker&rsquo;s local service appears offline. Saves that need the service will remain queued
                until it reconnects, and AI calls in flight may wait.
            </span>
            <button
                type="button"
                aria-label="Dismiss offline warning"
                onClick={() => setDismissed(true)}
                style={{
                    appearance: 'none',
                    border: '1px solid #92400e',
                    borderRadius: '6px',
                    background: 'transparent',
                    color: '#78350f',
                    cursor: 'pointer',
                    font: 'inherit',
                    padding: '3px 9px',
                    flexShrink: 0,
                }}
            >
                Dismiss
            </button>
        </div>
    );
}
